import { DEPRECATED_FACTORS, isFactorDeprecated } from '@parlay-picker/shared/deprecatedFactors';
import type { AnalysisConfig } from '@views/picks/constants';
import type { ReactElement } from 'react';
import { Section, SectionGroup } from './Section';
import { Toggle } from './Toggle';

interface AnalysisRulesSectionsProps {
  cfg: AnalysisConfig;
  expandedSections: Record<string, boolean>;
  toggleSection: (key: string) => void;
  getEnabledCount: (key: string) => number;
  getTotalCount: (key: string) => number;
  toggleAllInSection: (key: string, enable: boolean) => void;
  toggleAllTrends: (key: string, enable: boolean) => void;
  updateConfig: (section: string, key: string, value: boolean | number | string) => void;
  factorLimitProps: (
    checked: boolean,
    existingDisabled?: boolean,
    existingReason?: string,
  ) => { disabled: boolean; disabledReason: string | undefined };
  tier: string;
}

export function AnalysisRulesSections({
  cfg,
  expandedSections,
  toggleSection,
  getEnabledCount,
  getTotalCount,
  toggleAllInSection,
  toggleAllTrends,
  updateConfig,
  factorLimitProps,
  tier,
}: AnalysisRulesSectionsProps): ReactElement {
  const trendPaths = [
    'otherFactors.usageTrend',
    'otherFactors.minutesTrend',
    'otherFactors.efficiencyTrend',
  ];

  return (
    <SectionGroup
      title="Analysis Rules"
      subtitle="Controls how the AI interprets and weighs factors"
    >
      <Section
        title="Game Script"
        sectionKey="gameScript"
        isExpanded={expandedSections.gameScript}
        onToggleExpand={toggleSection}
        enabledCount={getEnabledCount('gameScript')}
        totalCount={getTotalCount('gameScript')}
        showToggleAll
        onToggleAll={toggleAllInSection}
      >
        <Toggle
          label="Blowout Risk"
          description="Warn when large spreads may cause starters to rest"
          checked={cfg.gameScript.blowoutRisk}
          onChange={(v) => updateConfig('gameScript', 'blowoutRisk', v)}
          {...factorLimitProps(cfg.gameScript.blowoutRisk)}
        />
        <Toggle
          label="Back-to-Back Detection"
          description="Flag games where teams played last night"
          checked={cfg.gameScript.backToBack}
          onChange={(v) => updateConfig('gameScript', 'backToBack', v)}
          {...factorLimitProps(cfg.gameScript.backToBack)}
        />
        <Toggle
          label="Pace Impact"
          description="Matchup pace analysis (fast/slow game environment)"
          checked={cfg.gameScript.paceImpact}
          onChange={(v) => updateConfig('gameScript', 'paceImpact', v)}
          {...factorLimitProps(cfg.gameScript.paceImpact)}
        />
        <Toggle
          label="Game Total"
          description="Use O/U total to gauge scoring environment"
          checked={cfg.gameScript.gameTotal}
          onChange={(v) => updateConfig('gameScript', 'gameTotal', v)}
          {...factorLimitProps(cfg.gameScript.gameTotal)}
        />
        <Toggle
          label="Home/Away Splits"
          description="Consider location-dependent performance patterns"
          checked={cfg.gameScript.homeAwaySplits}
          onChange={(v) => updateConfig('gameScript', 'homeAwaySplits', v)}
          {...factorLimitProps(cfg.gameScript.homeAwaySplits)}
        />
        <Toggle
          label="Revenge Game"
          description="Factor in recent blowout loss motivation"
          checked={cfg.gameScript.revengeGame}
          onChange={(v) => updateConfig('gameScript', 'revengeGame', v)}
          {...factorLimitProps(cfg.gameScript.revengeGame)}
        />
        <Toggle
          label="Team Momentum"
          description="Team L5 win/loss streaks — hot or cold (not player stats)"
          checked={cfg.gameScript.recentForm}
          onChange={(v) => updateConfig('gameScript', 'recentForm', v)}
          {...factorLimitProps(cfg.gameScript.recentForm)}
        />
      </Section>

      <Section
        title="Trend Analysis"
        sectionKey="trendAnalysis"
        isExpanded={expandedSections.trendAnalysis}
        onToggleExpand={toggleSection}
        enabledCount={[cfg.otherFactors.minutesTrend].filter((v) => v === true).length}
        totalCount={trendPaths.filter((p) => tier === 'admin' || !isFactorDeprecated(p)).length}
        showToggleAll
        onToggleAll={toggleAllTrends}
      >
        {tier === 'admin' && (
          <Toggle
            label="Usage Trend"
            description="Detect usage rate changes (L5 vs L10)"
            checked={false}
            onChange={() => {}}
            disabled
            disabledReason={DEPRECATED_FACTORS['otherFactors.usageTrend'].reason}
          />
        )}
        <Toggle
          label="Minutes Trend"
          description="Detect playing time changes (L5 vs L10)"
          checked={cfg.otherFactors.minutesTrend}
          onChange={(v) => updateConfig('otherFactors', 'minutesTrend', v)}
          {...factorLimitProps(cfg.otherFactors.minutesTrend)}
        />
        {tier === 'admin' && (
          <Toggle
            label="Efficiency Trend"
            description="Detect shooting efficiency changes (FG%/3PT% L5 vs L10)"
            checked={false}
            onChange={() => {}}
            disabled
            disabledReason={DEPRECATED_FACTORS['otherFactors.efficiencyTrend'].reason}
          />
        )}
      </Section>
    </SectionGroup>
  );
}
